import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useBranding, brandingFallback } from "@/lib/branding";
import { dashboardPathForRole } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Sheet, SheetContent, SheetTrigger,
} from "@/components/ui/sheet";
import {
    LogOut, User as UserIcon, LayoutDashboard, GraduationCap, Shield,
    LogIn, Menu,
} from "lucide-react";

const navLinkCls = ({ isActive }) =>
    `px-3 py-2 text-sm transition-colors hover:text-primary ${
        isActive ? "text-primary font-medium" : "text-foreground/70"
    }`;

export default function Header() {
    const { user, logout } = useAuth();
    const branding = useBranding();
    const navigate = useNavigate();

    const dashboardPath = dashboardPathForRole(user?.role);

    return (
        <header
            className="sticky top-0 z-40 border-b border-border glass-nav"
            data-testid="site-header"
        >
            <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
                {/* Logo */}
                <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
                    <img
                        src={branding.logo_url}
                        alt={branding.institution_name}
                        className="h-10 w-10 rounded-full object-contain" onError={brandingFallback}
                    />
                    <div className="leading-tight hidden sm:block">
                        <div className="font-display text-primary text-lg tracking-tight">
                            Heritage
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                            Community College · eLearning
                        </div>
                    </div>
                </Link>

                {/* Desktop nav */}
                <nav
                    className="hidden md:flex items-center gap-1 mx-auto"
                    data-testid="main-nav"
                >
                    <NavLink to="/" className={navLinkCls} end data-testid="nav-home">
                        Home
                    </NavLink>
                    <NavLink to="/about" className={navLinkCls} data-testid="nav-about">
                        About Us
                    </NavLink>
                    <NavLink to="/accuplacer" className={navLinkCls} data-testid="nav-accuplacer">
                        English Test
                    </NavLink>
                    <NavLink to="/apply" className={navLinkCls} data-testid="nav-apply">
                        Apply Now
                    </NavLink>
                    <NavLink to="/contact" className={navLinkCls} data-testid="nav-contact">
                        Contact Us
                    </NavLink>
                    {user && (
                        <NavLink
                            to={dashboardPath}
                            className={navLinkCls}
                            data-testid="nav-dashboard"
                        >
                            Dashboard
                        </NavLink>
                    )}
                </nav>

                {/* Right cluster */}
                <div className="flex items-center gap-2">
                    {!user ? (
                        <>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate("/login")}
                                data-testid="header-login-button"
                                className="gap-1.5"
                            >
                                <LogIn className="h-4 w-4" />
                                <span className="hidden sm:inline">Log In</span>
                            </Button>

                            <Button
                                size="sm"
                                onClick={() => navigate("/register")}
                                data-testid="header-register-button"
                                className="bg-primary hover:bg-primary/90 hidden sm:inline-flex"
                            >
                                Sign Up
                            </Button>
                        </>
                    ) : (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    data-testid="header-user-menu-trigger"
                                    className="gap-2"
                                >
                                    <UserIcon className="h-4 w-4" />
                                    <span className="hidden sm:inline max-w-[120px] truncate">
                                        {user.name}
                                    </span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel>
                                    <div className="flex flex-col">
                                        <span className="font-medium">{user.name}</span>
                                        <span className="text-xs text-muted-foreground capitalize">
                                            {user.role}
                                        </span>
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => navigate(dashboardPath)}
                                    data-testid="menu-dashboard"
                                >
                                    {user.role === "admin" ? (
                                        <Shield className="h-4 w-4 mr-2" />
                                    ) : user.role === "instructor" ? (
                                        <LayoutDashboard className="h-4 w-4 mr-2" />
                                    ) : (
                                        <GraduationCap className="h-4 w-4 mr-2" />
                                    )}
                                    Dashboard
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => {
                                        logout();
                                        navigate("/");
                                    }}
                                    data-testid="menu-logout"
                                >
                                    <LogOut className="h-4 w-4 mr-2" />
                                    Log Out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}

                    {/* Mobile nav */}
                    <Sheet>
                        <SheetTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="md:hidden"
                                data-testid="mobile-nav-trigger"
                                aria-label="Open menu"
                            >
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-72">
                            <div className="mt-8 flex flex-col gap-1">
                                <NavLink to="/" className={navLinkCls} end>
                                    Home
                                </NavLink>
                                <NavLink to="/about" className={navLinkCls}>
                                    About Us
                                </NavLink>
                                <NavLink to="/accuplacer" className={navLinkCls}>
                                    English Test
                                </NavLink>
                                <NavLink to="/contact" className={navLinkCls}>
                                    Contact Us
                                </NavLink>
                                {user && (
                                    <NavLink to={dashboardPath} className={navLinkCls}>
                                        Dashboard
                                    </NavLink>
                                )}
                                {!user && (
                                    <>
                                        <Button
                                            variant="outline"
                                            className="mt-6"
                                            onClick={() => navigate("/login")}
                                            data-testid="mobile-login-button"
                                        >
                                            <LogIn className="h-4 w-4 mr-2" />
                                            Log In
                                        </Button>
                                        <Button
                                            className="mt-2 bg-primary hover:bg-primary/90"
                                            onClick={() => navigate("/register")}
                                        >
                                            Sign Up
                                        </Button>
                                    </>
                                )}
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
